/**
 * Labels, badge variants, and formatters for the Accounts Payable module.
 * Badge variant strings match the `variant` prop on @/core/components/ui/badge.
 */
import { capitalize, uniqBy } from "lodash";
import { extractErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import { AP_ACTION_TYPE } from "./types";
import type {
  ApActionType,
  ApAgentAction,
  ApAgentRun,
  ApDocumentType,
  ApEmailStatus,
  ApEmail,
  ApEmailResponse,
  ApJobMatchStrategy,
  ApInvoice,
  ApInvoiceStatus,
  ApItemType,
  ApJobSearchResult,
  ApInvoiceItem,
  ApPage,
  ApPricebookItemOption,
  ApPurchaseDocument,
  ApPurchaseOrderOption,
  ApReceipt,
  ApReviewState,
} from "./types";

type BadgeVariant =
  | "green"
  | "blue"
  | "purple"
  | "magenta"
  | "gold"
  | "red"
  | "orange"
  | "aqua"
  | "gray";

interface StatusMeta {
  variant: BadgeVariant;
  label: string;
}

/** Routes as declared in app-content.tsx; build URLs via the helpers below. */
const AP_LIST_PATH = "/accounts-payable";
const INVOICE_REVIEW_PATH = `${AP_LIST_PATH}/invoices`;
const RECEIPT_REVIEW_PATH = `${AP_LIST_PATH}/receipts`;

/** The AP tabs. Each value doubles as its `?tab=` query-param value. */
export const AP_TAB = {
  review: "review",
  automated: "automated",
  invoices: "invoices",
  receipts: "receipts",
  emails: "emails",
  vendors: "vendors",
} as const;

export type ApTabValue = (typeof AP_TAB)[keyof typeof AP_TAB];

export const AP_TAB_LABEL: Record<ApTabValue, string> = {
  review: "Review",
  automated: "Automated",
  invoices: "Invoices",
  receipts: "Receipts",
  emails: "Emails",
  vendors: "Vendors",
};

export const AP_JOB_MATCH_STRATEGY_LABEL: Record<ApJobMatchStrategy, string> = {
  purchase_order: "matched by PO",
  job_number: "matched by job number",
  customer_name: "matched by customer",
  agent: "matched by agent",
  manual: "matched manually",
  non_job: "non-job expense",
};

/**
 * Human-readable label for how a job match happened. Falls back to the raw
 * value so an unrecognized strategy still renders something rather than
 * blanking out the field.
 */
export const jobMatchStrategyLabel = (
  strategy: ApJobMatchStrategy | null,
): string | null =>
  strategy ? (AP_JOB_MATCH_STRATEGY_LABEL[strategy] ?? strategy) : null;

/** Narrows a raw `?tab=` value; null when absent or unrecognized. */
export const parseApTab = (value: string | null): ApTabValue | null =>
  Object.values(AP_TAB).find((tab) => tab === value) ?? null;

export const apListPath = (tab?: ApTabValue | null) =>
  tab ? `${AP_LIST_PATH}?tab=${tab}` : AP_LIST_PATH;

/**
 * The invoice detail "review" page URL, carrying the tab it was opened from.
 * The page reads it to pick the back destination and to decide whether the
 * review-queue carousel applies.
 */
export const invoiceReviewPath = (
  invoiceId: string,
  fromTab?: ApTabValue | null,
) =>
  fromTab
    ? `${INVOICE_REVIEW_PATH}/${invoiceId}?tab=${fromTab}`
    : `${INVOICE_REVIEW_PATH}/${invoiceId}`;

/** The receipt detail "review" page URL; see `invoiceReviewPath`. */
export const receiptReviewPath = (
  receiptId: string,
  fromTab?: ApTabValue | null,
) =>
  fromTab
    ? `${RECEIPT_REVIEW_PATH}/${receiptId}?tab=${fromTab}`
    : `${RECEIPT_REVIEW_PATH}/${receiptId}`;

/**
 * Accessible name for a document preview: vendor, kind and number, with a
 * missing number (common on receipts) left out rather than read as "null".
 */
export const documentLabel = (
  document: Pick<
    ApPurchaseDocument,
    "vendor_name" | "document_type" | "document_number"
  >,
): string =>
  [
    document.vendor_name,
    document.document_type ?? "invoice",
    document.document_number,
  ]
    .filter(Boolean)
    .join(" ");

/** How copy names a document kind. */
export const documentTypeLabel = (documentType: ApDocumentType) =>
  ({
    invoice: { lower: "invoice", capitalized: "Invoice" },
    receipt: { lower: "receipt", capitalized: "Receipt" },
  })[documentType];

/**
 * "MASTERCARD •••• 5398", "CASH", or null when the receipt printed nothing
 * about how it was paid.
 */
export const receiptPaymentLabel = (
  receipt: Pick<ApReceipt, "payment_method" | "card_last4">,
): string | null =>
  [receipt.payment_method, receipt.card_last4 && `•••• ${receipt.card_last4}`]
    .filter(Boolean)
    .join(" ") || null;

// The pipeline's in-between states all read as one neutral "Processing".
export const INVOICE_STATUS: Record<ApInvoiceStatus, StatusMeta> = {
  pending_parse: { variant: "gray", label: "Processing" },
  parsing: { variant: "gray", label: "Processing" },
  parsed: { variant: "gray", label: "Processing" },
  matching: { variant: "gray", label: "Processing" },
  matched: { variant: "green", label: "Matched to job" },
  match_failed: { variant: "red", label: "Needs a job" },
  parse_failed: { variant: "red", label: "Couldn't read" },
};

// "awaiting_agent" has no badge: until the agent has run there is nothing to
// review, and the status column already says what is happening.
export const REVIEW_STATE: Record<
  Exclude<ApReviewState, "awaiting_agent">,
  StatusMeta
> = {
  needs_review: { variant: "gold", label: "Needs review" },
  reviewed: { variant: "green", label: "Reviewed" },
};

/** The badge for a review state, or null when there is nothing to show. */
export const reviewStateMeta = (
  state: ApReviewState | undefined,
): StatusMeta | null =>
  state && state !== "awaiting_agent" ? REVIEW_STATE[state] : null;

const NON_JOB_STATUS: StatusMeta = {
  variant: "green",
  label: "Non-job expense",
};

type InvoiceStatusFields = {
  status: ApInvoiceStatus;
  st_job_external_id?: number | null;
};

/** Settled with no job: the agent's `non_job` disposition or a job-less PO match. */
export const isNonJobExpense = (invoice: InvoiceStatusFields) =>
  invoice.status === "matched" && !invoice.st_job_external_id;

export const invoiceStatusMeta = (invoice: InvoiceStatusFields): StatusMeta =>
  isNonJobExpense(invoice) ? NON_JOB_STATUS : INVOICE_STATUS[invoice.status];

/**
 * Run-level badge for fully automated runs. "Handled automatically" describes
 * the handling (proposals approved without human review) rather than implying
 * a different kind of run; purple so it can't be mistaken for an action state.
 */
export const AUTOMATED_RUN_BADGE: StatusMeta = {
  variant: "purple",
  label: "Handled automatically",
};

export const ACTION_TYPE: Record<ApActionType, StatusMeta> = {
  [AP_ACTION_TYPE.matchJob]: { variant: "blue", label: "Match job" },
  [AP_ACTION_TYPE.markNonJob]: { variant: "blue", label: "Mark non-job" },
  [AP_ACTION_TYPE.linkVendor]: { variant: "aqua", label: "Link vendor" },
  [AP_ACTION_TYPE.mergeVendor]: {
    variant: "aqua",
    label: "Link vendor (merge)",
  },
  [AP_ACTION_TYPE.updatePricebookCost]: {
    variant: "gold",
    label: "Update pricebook cost",
  },
  [AP_ACTION_TYPE.createPricebookItem]: {
    variant: "purple",
    label: "Create pricebook item",
  },
  [AP_ACTION_TYPE.updatePo]: { variant: "orange", label: "Update PO" },
  [AP_ACTION_TYPE.createPo]: { variant: "magenta", label: "Create PO" },
  [AP_ACTION_TYPE.createReturn]: { variant: "red", label: "Create return" },
  [AP_ACTION_TYPE.flagForReview]: {
    variant: "gold",
    label: "Flag for review",
  },
  [AP_ACTION_TYPE.noAction]: { variant: "gray", label: "Reviewed, no change" },
};

/**
 * Badge meta for an action type. An unknown action type (e.g. when the backend
 * ships a new one first) degrades to a plain gray tag.
 */
export const actionTypeMeta = (actionType: ApActionType): StatusMeta =>
  (ACTION_TYPE as Partial<Record<string, StatusMeta>>)[actionType] ?? {
    variant: "gray",
    label: capitalize(actionType.replaceAll("_", " ")),
  };

/**
 * Whether reviewers may edit an action's payload before approving.
 *
 * `merge_vendor`: also see `Ap::AgentAction::SYSTEM_DERIVED_ACTION_TYPES` in
 *   the `api` repo; its payload is written by the system, not the agent, and
 *   the backend rejects edits to it with a 400.
 */
const EDITABLE_PAYLOAD: Record<ApActionType, boolean> = {
  [AP_ACTION_TYPE.matchJob]: true,
  [AP_ACTION_TYPE.markNonJob]: true,
  [AP_ACTION_TYPE.linkVendor]: true,
  [AP_ACTION_TYPE.mergeVendor]: false,
  [AP_ACTION_TYPE.updatePricebookCost]: true,
  [AP_ACTION_TYPE.createPricebookItem]: true,
  [AP_ACTION_TYPE.updatePo]: true,
  [AP_ACTION_TYPE.createPo]: true,
  [AP_ACTION_TYPE.createReturn]: true,
  [AP_ACTION_TYPE.flagForReview]: true,
  [AP_ACTION_TYPE.noAction]: true,
};

/** Friendly labels for the ServiceTitan PO fields the agent can change. */
const PO_FIELD_LABELS: Record<string, string> = {
  vendorInvoiceNumber: "Vendor invoice #",
  memo: "Memo",
  shipTo: "Ship to",
  requiredOn: "Required by",
  total: "Total",
  vendorId: "Vendor",
  businessUnitId: "Business unit",
};
export const humanizePoField = (key: string) =>
  PO_FIELD_LABELS[key] ??
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const hasEditablePayload = (action: ApAgentAction) =>
  // Unknown types have no edit form either.
  (EDITABLE_PAYLOAD as Partial<Record<string, boolean>>)[action.action_type] ??
  false;

export const EMAIL_STATUS: Record<ApEmailStatus, StatusMeta> = {
  received: { variant: "gray", label: "Received" },
  processing: { variant: "blue", label: "Processing" },
  processed: { variant: "green", label: "Processed" },
  partial_failure: { variant: "gold", label: "Partial failure" },
  failed: { variant: "red", label: "Failed" },
};

/** Format a dollar amount (already in dollars, not cents). */
export const formatDollars = (n: number): string =>
  `$${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Format a percentage from a 0–1 confidence value. */
export const formatPercent = (value: number): string =>
  `${Math.round(value * 100)}%`;

/**
 * The confidence at or above which the queue offers to approve in bulk. Lives
 * here so the row and the bulk button read the same number — they previously
 * disagreed, and a row could look settled while the button skipped it.
 */
export const HIGH_CONFIDENCE = 0.9;

/**
 * Below this the agent was choosing between plausible candidates rather than
 * agreeing with itself — its own instruction is "0.5 or below when you picked
 * the best of several". These are the rows that repay a closer read.
 */
export const LOW_CONFIDENCE = 0.7;

export type ApConfidenceTone = "low" | "normal" | "high";

export const confidenceTone = (value: number): ApConfidenceTone => {
  if (value < LOW_CONFIDENCE) return "low";
  return value >= HIGH_CONFIDENCE ? "high" : "normal";
};

/**
 * Confidence as prose. The figure is the agent's own assessment of the evidence
 * behind a proposal, not a measurement, so it reads as text rather than as a
 * bar. It stands alone: `actionTitle` already names the action, which made an
 * action-type label beside it a restatement. A low figure is set in a heavier
 * weight by the row rather than annotated in words.
 */
export const confidenceLabel = (value: number): string =>
  `${formatPercent(value)} confidence`;

/**
 * Text colour for a confidence figure. gold-60, not the gold-50 meter fill,
 * which is too light to clear AA as text.
 */
export const CONFIDENCE_TEXT_CLASS: Record<ApConfidenceTone, string> = {
  high: "text-interactive-text",
  normal: "text-gold-60",
  low: "text-negative-text",
};

export const formatApDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "—";
};

const inZone = (value: dayjs.Dayjs, timeZone?: string) =>
  timeZone ? value.tz(timeZone) : value;

/** A timestamp as its calendar day in the business's time zone. */
export const formatApZonedDate = (
  value: string | null | undefined,
  timeZone?: string,
): string => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid()
    ? inZone(parsed, timeZone).format("MMM D, YYYY")
    : "—";
};

/** The same timestamp with its time and zone, for a tooltip. */
export const formatApZonedDateTime = (
  value: string | null | undefined,
  timeZone?: string,
): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid()
    ? inZone(parsed, timeZone).format("MMM D, YYYY h:mm A z")
    : undefined;
};

/** Today's date (YYYY-MM-DD) in the business's time zone. */
export const apToday = (timeZone?: string): string =>
  inZone(dayjs(), timeZone).format("YYYY-MM-DD");

/**
 * What an empty Invoices/Receipts list says, given what narrowed it. A day
 * with nothing added or reviewed yet is the common case once Today is picked,
 * so it gets its own wording rather than the generic "no matches".
 */
export const documentListEmptyMessage = (
  kind: ApDocumentType,
  {
    range,
    field = "added",
    filtered,
    today,
  }: {
    range: { from: string; to: string } | null;
    field?: "added" | "reviewed";
    filtered: boolean;
    today: string;
  },
): { title: string; body: string } => {
  const plural = `${kind}s`;
  if (range) {
    const day = (value: string) => dayjs(value, "YYYY-MM-DD").format("MMM D");
    const when =
      range.from === range.to
        ? range.from === today
          ? "today"
          : `on ${day(range.from)}`
        : `between ${day(range.from)} and ${day(range.to)}`;
    const verb = field === "reviewed" ? "reviewed" : "added";
    const filterName = field === "reviewed" ? "Reviewed" : "Added";
    return {
      title: `No ${filtered ? "matching " : ""}${plural} ${verb} ${when}`,
      body: `Pick another day, or clear the ${filterName} filter to see them all.`,
    };
  }
  if (filtered) {
    return {
      title: `No ${plural} match these filters`,
      body: "Try another status or search.",
    };
  }
  return {
    title: `No ${plural} yet`,
    body: `${capitalize(plural)} emailed to your AP address or uploaded here will show up in this list.`,
  };
};

export const formatApDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D · h:mm a") : "—";
};

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const enrichApEmail = (email: ApEmailResponse): ApEmail => ({
  ...email,
  from: email.from ?? email.from_address ?? "",
  message:
    email.plain_body?.trim() ||
    email.html_body?.replace(/<[^>]+>/g, " ").trim(),
  invoiceCount: email.attachments.reduce(
    (count, attachment) => count + (attachment.invoice_ids?.length ?? 0),
    0,
  ),
  receiptCount: email.attachments.reduce(
    (count, attachment) => count + (attachment.receipt_ids?.length ?? 0),
    0,
  ),
});

export const fileKind = (contentType: string): string => {
  if (contentType === "application/pdf") return "PDF";
  if (contentType.startsWith("image/"))
    return contentType.split("/")[1].toUpperCase();
  return contentType;
};

/* ServiceTitan deep-link paths (appended to the tenant's serviceTitanBaseUrl).
   Patterns match the backend's admin_helper.rb. */
export const stJobUrl = (id: number) => `/#/Job/Index/${id}`;
export const stPurchaseOrderUrl = (id: number) =>
  `/#/new/inventory/purchase-orders/details/${id}`;
export const stPricebookItemUrl = (type: ApItemType, id: number) =>
  `/#/Settings/Pricebook/${
    type === "equipment" ? "Equipment" : "Materials"
  }/Edit/${id}`;

export type PricebookCostChange = {
  direction: "raise" | "lower" | "none";
  /** Percent change from the current cost; null when there is nothing to compare against. */
  pct: number | null;
};

// Direction and size of a proposed pricebook cost change.
export const pricebookCostChange = (
  current: number,
  proposed: number,
): PricebookCostChange => {
  /**
   * Edge cases:
   * - no current cost to compare against (unlisted vendor / inactive cost)
   * - current and proposed are somehow equal (unexpected but possible)
   */
  if (!(current > 0)) return { direction: "none", pct: null };
  if (current === proposed) return { direction: "none", pct: 0 };

  return {
    direction: proposed > current ? "raise" : "lower",
    pct: ((proposed - current) / current) * 100,
  };
};

const COST_CHANGE_VERB: Record<PricebookCostChange["direction"], string> = {
  raise: "Raise",
  lower: "Lower",
  none: "Update",
};

/** Human-readable headline for an agent action. */
export const actionTitle = (a: ApAgentAction): string => {
  const p = a.payload;
  switch (a.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return `Match to ServiceTitan job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.linkVendor:
    case AP_ACTION_TYPE.mergeVendor:
      return `Link to ST vendor "${p.st_vendor_name}"`;
    case AP_ACTION_TYPE.updatePricebookCost:
      // Verb from the shared pricebookCostChange; keep the before→after figures
      // so the Review row shows the magnitude, not just the direction.
      return `${COST_CHANGE_VERB[pricebookCostChange(p.current_cost, p.proposed_cost).direction]} cost of ${
        p.sku
      }: ${formatDollars(p.current_cost)} → ${formatDollars(p.proposed_cost)}`;
    case AP_ACTION_TYPE.createPricebookItem:
      return p.adopted_item
        ? `Use existing pricebook item ${p.adopted_item.code}`
        : `Add "${p.name}" to pricebook`;
    case AP_ACTION_TYPE.updatePo:
      return `Update purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.markNonJob:
      return "Mark as a non-job expense";
    case AP_ACTION_TYPE.createPo:
      return p.non_job
        ? "Create purchase order with no job"
        : `Create purchase order on job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.createReturn:
      return `Create return on job #${p.st_job_external_id}`;
    // Both no-op types carry the whole point of the row in `reason`; the
    // constants are only a fallback for a payload that arrives without one.
    case AP_ACTION_TYPE.flagForReview:
      return p.reason || "Flag for human review";
    case AP_ACTION_TYPE.noAction:
      return p.reason || "No action needed";
    default:
      return "Proposed action";
  }
};

/**
 * The action's category as an eyebrow — mostly the static type label, but a
 * pricebook cost change carries its direction (raise/lower) so the detail line
 * beneath can drop the verb without losing which way the cost moved, and a
 * pricebook item that was already there says it was reused, not created.
 */
export const actionCategoryLabel = (a: ApAgentAction): string => {
  if (
    a.action_type === AP_ACTION_TYPE.createPricebookItem &&
    a.payload.adopted_item
  ) {
    return "Use existing pricebook item";
  }
  if (a.action_type === AP_ACTION_TYPE.updatePricebookCost) {
    const { direction } = pricebookCostChange(
      a.payload.current_cost,
      a.payload.proposed_cost,
    );
    return `${COST_CHANGE_VERB[direction]} cost`;
  }
  return actionTypeMeta(a.action_type).label;
};

/**
 * The specific object of an action with the category verb stripped — pairs with
 * `actionCategoryLabel` as the "what" beneath the eyebrow, so the two don't
 * restate each other (the title's "Match to ... job" over a "Match job" label).
 */
export const actionDetail = (a: ApAgentAction): string => {
  const p = a.payload;
  switch (a.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return `ServiceTitan job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.linkVendor:
    case AP_ACTION_TYPE.mergeVendor:
      return `"${p.st_vendor_name}"`;
    case AP_ACTION_TYPE.updatePricebookCost:
      return `${p.sku} · ${formatDollars(p.current_cost)} → ${formatDollars(
        p.proposed_cost,
      )}`;
    case AP_ACTION_TYPE.createPricebookItem:
      return p.adopted_item
        ? `"${p.adopted_item.name ?? p.adopted_item.code}"`
        : `"${p.name}"`;
    case AP_ACTION_TYPE.updatePo:
      return `Purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.markNonJob:
      return p.reason || "Non-job expense";
    case AP_ACTION_TYPE.createPo:
      return p.non_job
        ? "Purchase order with no job"
        : `Job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.createReturn:
      return `Job #${p.st_job_external_id}`;
    // The no-op types carry their point in `reason`, which is the detail here.
    case AP_ACTION_TYPE.flagForReview:
      return p.reason || "Flag for human review";
    case AP_ACTION_TYPE.noAction:
      return p.reason || "Reviewed, no change";
    default:
      return "Proposed action";
  }
};

export const isPendingReviewAction = (action: ApAgentAction) =>
  action.status === "pending_review";

/**
 * The run's actions this action depends on (the API's
 * `prerequisite_action_ids`): for a PO, the `create_pricebook_item` actions
 * whose new items its lines carry.
 */
export const getPrerequisiteActions = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
): ApAgentAction[] =>
  (action.prerequisite_action_ids ?? []).flatMap((id) =>
    runActions.filter((a) => a.id === id),
  );

/**
 * The PO's new pricebook items that approving it also approves: its
 * prerequisites still awaiting review. Items already decided on their own
 * card are left out, so the list and the approve button count the same ones.
 */
export const getNewItemsToCreate = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) => getPrerequisiteActions(action, runActions).filter(isPendingReviewAction);

/**
 * Whether a pending action in the run lists this one as a prerequisite and
 * can still be approved, so approving it approves this one too.
 */
export const isApprovedWithPendingAction = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) =>
  runActions.some(
    (a) =>
      isPendingReviewAction(a) &&
      !a.approval_blocked_reason &&
      (a.prerequisite_action_ids ?? []).includes(action.id),
  );

/**
 * The approve button's label: a PO that would also approve pending
 * pricebook items says it creates them. Shared by the review card and the
 * Review tab row so both say the same thing.
 */
export const approveLabel = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
): string => {
  if (action.approval_blocked_reason) return "Approve";

  const count = getNewItemsToCreate(action, runActions).length;
  return count > 0
    ? `Approve + create ${count} item${count === 1 ? "" : "s"}`
    : "Approve";
};

/**
 * Whether "Approve N high-confidence" may include this action: pending,
 * confident, approvable, and every pending action its approval brings along
 * is confident too. Approving a PO approves its new pricebook items whatever
 * their confidence, so a low-confidence item would otherwise slip through.
 */
export const isHighConfidenceApprovable = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) =>
  isPendingReviewAction(action) &&
  action.confidence >= HIGH_CONFIDENCE &&
  !action.approval_blocked_reason &&
  getNewItemsToCreate(action, runActions).every(
    (item) => item.confidence >= HIGH_CONFIDENCE,
  );

/**
 * Whether an action writes nothing when approved. The API applies these
 * without a handler (`Ap::Agent::Actions` registers none), so approving one
 * only acknowledges it — which is why they get their own control.
 */
export const isNoOpAction = (action: ApAgentAction) =>
  action.action_type === AP_ACTION_TYPE.flagForReview ||
  action.action_type === AP_ACTION_TYPE.noAction;

/** Who decided an action - a derived value, not directly exposed by the API. */
export type ApActionProvenance = "undecided" | "human" | "automation";

/**
 * System decisions leave `reviewed_by` null; human approvals/rejections always
 * set it. A reviewer with a blank display name serializes as "", which is still
 * a human review — hence the strict null comparison.
 */
export const actionProvenance = (action: ApAgentAction): ApActionProvenance => {
  if (isPendingReviewAction(action)) return "undecided";
  return action.reviewed_by === null ? "automation" : "human";
};

export const isAutomatedRun = (run: ApAgentRun) =>
  run.automation_qualification === "qualified_selected";

/**
 * Short outcome line for the Automated log. No provenance — everything on that
 * tab was auto-approved — and null on a clean apply (the green glyph already
 * says so), so only deviations speak up. Order mirrors ActionStatusGlyph.
 *
 * Only an apply failure is a real "the change didn't land" the user should act
 * on; a rollback is Admin-only and a rollback failure is often a legitimate
 * "the data moved on, so it can't be reversed" — so those read as muted info,
 * not errors (see the row's tone and the glyph color).
 */
export const automatedOutcomeCopy = (action: ApAgentAction): string | null => {
  if (action.rollback_status === "rolled_back")
    return `Reversed on ${formatApDate(action.rolled_back_at)}`;
  if (action.rollback_status === "rollback_failed")
    return "Couldn't be reversed";
  if (action.status === "apply_failed")
    return "Couldn't be applied in ServiceTitan";
  if (action.status === "rejected") return "Rejected";
  if (action.status === "approved" || action.status === "applying")
    return "Applying…";
  return null;
};

/** "automatically" or "by <reviewer>" — who settled a resolved action. */
const decidedBy = (action: ApAgentAction): string =>
  actionProvenance(action) === "automation"
    ? "automatically"
    : `by ${action.reviewed_by?.trim() || "a teammate"}`;

/**
 * A resolved action's outcome line on the review page, the invoice sidebar and
 * the queue preview. Decisions name who made them, in the words of the button
 * that made them: a flag reads "Marked as seen" / "Ignored", anything else
 * "Rejected". Otherwise only deviations from a clean apply speak up (the
 * Automated tab's rule). Null while pending.
 */
export const actionOutcomeCopy = (action: ApAgentAction): string | null => {
  if (isPendingReviewAction(action)) return null;
  if (isNoOpAction(action))
    return action.status === "rejected"
      ? `Ignored ${decidedBy(action)}`
      : `Marked as seen ${decidedBy(action)}`;
  if (action.status === "rejected") return `Rejected ${decidedBy(action)}`;
  return automatedOutcomeCopy(action);
};

/**
 * The ids "Approve all" sends: every pending action except a blocked one,
 * which the API would skip and which stays pending.
 */
export const getBulkApproveIds = (actions: ApAgentAction[]) =>
  actions
    .filter(
      (action) =>
        isPendingReviewAction(action) && !action.approval_blocked_reason,
    )
    .map((action) => action.id);

/**
 * Whether an "Approve all" approves this action: pending, a real approval
 * rather than an acknowledgement, and not blocked (the API skips a blocked one).
 */
export const isBulkApprovable = (action: ApAgentAction) =>
  isPendingReviewAction(action) &&
  !isNoOpAction(action) &&
  !action.approval_blocked_reason;

/**
 * Flatten infinite-query pages into one list, deduping by id — offset paging
 * over a live list can serve an item on two loaded pages when rows arrive or
 * leave between fetches.
 */
export const flattenPages = <T extends { id: string }>(
  pages: ApPage<T>[] | undefined,
): T[] => uniqBy(pages?.flatMap((page) => page.items) ?? [], (item) => item.id);

/**
 * The run whose actions are live for review. Runs arrive newest-first, and a
 * newer in-flight or failed run doesn't supersede the completed one's
 * proposals — so never take `runs[0]` unfiltered.
 */
export const getLatestCompletedRun = (runs: ApAgentRun[]) =>
  runs.find((run) => run.status === "completed");

/**
 * A run that has been created but hasn't produced its proposals yet. It carries
 * no actions, so `getLatestCompletedRun` can't see it — without this the UI goes
 * blank between kicking a run off (e.g. a manual job match) and it completing.
 */
export const isActiveRun = (run: ApAgentRun) =>
  run.status === "pending" || run.status === "running";

export const getActiveRun = (runs: ApAgentRun[]) => runs.find(isActiveRun);

/** Poll interval while a run is still working; false once it settles. */
export const ACTIVE_RUN_POLL_MS = 3000;

/**
 * Whether a document's runs are still moving on their own: a run is pending
 * or running, an action is mid-flight to ServiceTitan (approved → applying →
 * applied/apply_failed), or one is mid-rollback (rolling_back → rolled_back/
 * rollback_failed). Views showing the runs or the review state poll while
 * this holds, so they reach the settled state without a manual refresh.
 */
export const areRunsInFlight = (runs: ApAgentRun[]) =>
  runs.some(
    (run) =>
      isActiveRun(run) ||
      (run.actions ?? []).some(
        (action) =>
          action.status === "approved" ||
          action.status === "applying" ||
          action.rollback_status === "rolling_back",
      ),
  );

/** `refetchInterval` for a document's runs query: poll while in flight. */
export const runsInFlightRefetchInterval = (query: {
  state: { data?: ApAgentRun[] };
}) => (areRunsInFlight(query.state.data ?? []) ? ACTIVE_RUN_POLL_MS : false);

/* ------------------------------------------------------- manual job matching */

/**
 * Shortest query the job search accepts, mirroring the backend's
 * `Ap::JobSearchService::MIN_QUERY_LENGTH` — it answers anything shorter with
 * an empty list, so the UI doesn't spend a request on it either. Three rather
 * than two: a two-character query is the most expensive search ServiceTitan
 * can be asked for and the least likely to be the one the user meant.
 */
export const MIN_JOB_SEARCH_LENGTH = 3;

/** Shortest query the pricebook search runs: a code can be two characters. */
export const MIN_PRICEBOOK_SEARCH_LENGTH = 2;

/** The name a user knows an item by, falling back to its code. */
export const pricebookItemLabel = (option: ApPricebookItemOption) =>
  option.name ?? option.code ?? `item ${option.external_id}`;

/** Code, type and cost: what tells two similar items apart. */
export const pricebookOptionMeta = (option: ApPricebookItemOption) =>
  [
    option.code,
    option.item_type === "equipment" ? "Equipment" : "Material",
    option.cost != null ? formatDollars(Number(option.cost)) : null,
  ].filter((part): part is string => !!part);

/**
 * Whether a line is matched to a pricebook item. The id decides: an item can
 * have no name, and a match to one must still read as a match. The name also
 * counts, for lines that carry only a name (the AP demo data).
 */
export const hasPricebookMatch = (item: ApInvoiceItem) =>
  item.matched_pricebook_item_id != null ||
  item.matched_pricebook_item_name != null;

/** The matched item's name, or its id when the item has no name. */
export const matchedPricebookItemLabel = (item: ApInvoiceItem) =>
  item.matched_pricebook_item_name ??
  (item.matched_pricebook_item_id != null
    ? `Item ${item.matched_pricebook_item_id}`
    : null);

/**
 * The invoiced SKU a pricebook proposal is about — what a reprice was raised
 * for, or the code a new item would carry — or null for any other action.
 */
export const pricebookProposalSku = (action: ApAgentAction): string | null => {
  if (action.action_type === AP_ACTION_TYPE.updatePricebookCost)
    return action.payload?.sku ?? null;
  if (action.action_type === AP_ACTION_TYPE.createPricebookItem)
    return action.payload?.code ?? null;
  return null;
};

/**
 * The label of a pricebook proposal's way out — picking the line's item by
 * hand — or null when the proposal names no line on the document to pick for.
 */
export const pricebookProposalLinkLabel = (action: ApAgentAction) => {
  if (!pricebookProposalSku(action) || !action.line_item_ids?.length)
    return null;
  // A new item for a line already stocked; otherwise a reprice of the wrong item.
  return action.action_type === AP_ACTION_TYPE.createPricebookItem
    ? "Use an existing item"
    : "Wrong item?";
};

/**
 * A PO proposal's way out — picking the document's PO by hand — or null for
 * any other action. The picker opens on the job's POs when there is a job to
 * list; an update always has a PO on file to show.
 */
export const purchaseOrderProposalLink = (
  action: ApAgentAction,
  documentHasJob: boolean,
) => {
  if (action.action_type === AP_ACTION_TYPE.createPo)
    return { label: "Use an existing PO", hasJob: documentHasJob };
  if (action.action_type === AP_ACTION_TYPE.updatePo)
    return { label: "Wrong PO?", hasJob: true };
  return null;
};

/**
 * An accessible name that starts with the visible words (so voice control
 * finds it) and then says which document it acts on, since a review list
 * repeats the same link on every row.
 */
export const proposalLinkAriaLabel = (label: string, subject: string) =>
  `${label.replace(/\?$/, "")}: ${subject}`;

/** An invoice's or receipt's extracted lines. */
export const documentLineItems = (document: ApInvoice | ApReceipt) =>
  document.document_type === "receipt"
    ? document.receipt_items
    : document.invoice_items;

/** Why there is nothing to pick from yet, or null when the results show. */
export const pricebookSearchStatusMessage = ({
  query,
  isLoading,
  isError,
  resultCount,
}: {
  query: string;
  isLoading: boolean;
  isError: boolean;
  resultCount: number;
}): string | null => {
  if (query.trim().length < MIN_PRICEBOOK_SEARCH_LENGTH)
    return `Type at least ${MIN_PRICEBOOK_SEARCH_LENGTH} characters to search the pricebook.`;
  if (isLoading) return "Searching the pricebook…";
  if (isError) return "Couldn’t search the pricebook. Try again.";
  if (resultCount === 0) return "No pricebook items match that search.";
  return null;
};

/**
 * The api keeps the POs whose number starts with the query (ServiceTitan
 * itself matches anywhere in the number), so a shorter query is too broad to
 * be useful (the api's `MIN_QUERY_LENGTH`). Blank is fine: it lists the POs on
 * the document's own job.
 */
export const MIN_PO_SEARCH_LENGTH = 3;

/** The PO number a user knows, falling back to ServiceTitan's id. */
export const purchaseOrderLabel = (option: ApPurchaseOrderOption) =>
  option.number ? `PO ${option.number}` : `PO #${option.st_purchase_order_id}`;

/** Status, job, vendor, total and date: what tells two POs apart. */
export const purchaseOrderOptionMeta = (option: ApPurchaseOrderOption) =>
  [
    option.status,
    option.st_job_external_id
      ? `Job ${option.job_number ?? `#${option.st_job_external_id}`}`
      : "No job",
    option.vendor_name ??
      (option.st_vendor_id ? `Vendor #${option.st_vendor_id}` : null),
    option.total != null ? formatDollars(option.total) : null,
    // ServiceTitan dates a PO at midnight UTC; read the calendar day, or it
    // shows as the day before anywhere west of UTC.
    option.date ? formatApDate(option.date.slice(0, 10)) : null,
  ].filter((part): part is string => !!part);

/** The list's empty, loading and error states; null when there are rows. */
export const purchaseOrderSearchStatusMessage = ({
  query,
  hasJob,
  isLoading,
  isError,
  resultCount,
}: {
  query: string;
  /** Whether the document has a job, whose POs a blank query lists. */
  hasJob: boolean;
  isLoading: boolean;
  isError: boolean;
  resultCount: number;
}): string | null => {
  const q = query.trim();
  if (q.length > 0 && q.length < MIN_PO_SEARCH_LENGTH)
    return `Type at least ${MIN_PO_SEARCH_LENGTH} characters of a PO or job number.`;
  if (isLoading) return "Looking up purchase orders in ServiceTitan…";
  if (isError) return "Couldn’t reach ServiceTitan. Try again.";
  if (resultCount > 0) return null;
  if (q) return "No purchase orders match that PO or job number.";
  return hasJob
    ? "No purchase orders on this job yet. Search by PO or job number."
    : "Search by PO or job number.";
};

/** Whether a person chose the document's PO, or said it has none. */
export const hasManualPurchaseOrder = (
  document: Pick<ApPurchaseDocument, "po_match_strategy">,
): boolean => document.po_match_strategy === "manual";

/** How many job matches to ask for — one comfortable scroll, no paging. */
export const JOB_SEARCH_LIMIT = 20;

/**
 * The invoice states where manual attribution is offered: automatic matching
 * has run its course (or produced nothing) and there's an extracted invoice to
 * attribute. `matched` is included so a confidently-wrong automatic match can
 * be corrected — the server supersedes the outstanding proposals when the job
 * changes. `matching` is deliberately absent — a match is still in flight.
 */
const MANUAL_JOB_MATCH_STATUSES: ApInvoiceStatus[] = [
  "parsed",
  "matched",
  "match_failed",
];

/**
 * Whether to offer manual job attribution for an invoice. Status alone decides:
 * an already-attached job is a candidate for correction, not a reason to
 * withhold the escape hatch.
 */
export const canManuallyMatchJob = (
  invoice: Pick<ApInvoice, "status">,
): boolean => MANUAL_JOB_MATCH_STATUSES.includes(invoice.status);

/**
 * The trailing meta line for a job option — status, completion date, business
 * unit and total, with blanks dropped so a sparse job never renders a dangling
 * separator.
 */
export const jobSearchMeta = (job: ApJobSearchResult): string[] =>
  [
    job.status,
    job.completed_on ? formatApDate(job.completed_on) : null,
    job.business_unit,
    job.total === null ? null : formatDollars(job.total),
  ].filter((part): part is string => Boolean(part));

/**
 * The one message that stands in for the results list, or null when the
 * matches themselves should show. Ordered so a stale error can't hide the
 * "keep typing" hint, and so a refetch over existing results stays quiet —
 * `isLoading` is the first-load flag, not "a request is in flight".
 *
 * A throttled search gets its own wording: it is the one failure that would
 * otherwise read as "no jobs match that search", telling the user the job
 * doesn't exist when ServiceTitan merely refused to look.
 */
export const jobSearchStatusMessage = ({
  query,
  isLoading,
  isError,
  isRateLimited = false,
  resultCount,
}: {
  query: string;
  isLoading: boolean;
  isError: boolean;
  isRateLimited?: boolean;
  resultCount: number;
}): string | null => {
  if (query.trim().length < MIN_JOB_SEARCH_LENGTH)
    return `Type at least ${MIN_JOB_SEARCH_LENGTH} characters to search jobs.`;
  if (isLoading) return "Searching jobs…";
  if (isRateLimited)
    return "ServiceTitan is busy right now. Wait a moment and search again.";
  if (isError) return "Couldn’t search ServiceTitan jobs. Please try again.";
  if (resultCount === 0) return "No jobs match that search.";
  return null;
};

/**
 * True when a job search failed because ServiceTitan throttled it (HTTP 429),
 * which the UI reports differently from a generic failure.
 */
export const isJobSearchRateLimited = (error: unknown): boolean =>
  error !== null && extractErrorMessage(error).status === 429;
